/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

const systemTypes: Record<string, { label: string; costRange: string; avgInstallHrs: number }> = {
  split_ac: { label: "Split System A/C", costRange: "$3,500-7,000", avgInstallHrs: 14 },
  heat_pump: { label: "Heat Pump", costRange: "$4,000-8,000", avgInstallHrs: 16 },
  furnace_ac: { label: "Furnace + A/C", costRange: "$5,500-10,000", avgInstallHrs: 18 },
  mini_split: { label: "Mini-Split (per head)", costRange: "$1,500-4,000", avgInstallHrs: 8 },
  package_unit: { label: "Package Unit / RTU", costRange: "$4,000-12,000", avgInstallHrs: 20 },
};

const ductMaterials: Record<string, { costPerFt: number; label: string }> = {
  flex_6: { costPerFt: 1.5, label: '6" Flex Duct' },
  flex_8: { costPerFt: 2.0, label: '8" Flex Duct' },
  sheet_metal: { costPerFt: 8.0, label: "Sheet Metal (avg)" },
  spiral: { costPerFt: 6.5, label: "Spiral Round" },
};

export function HvacCalculatorClient() {
  const [systemType, setSystemType] = useState("furnace_ac");
  const [systemCount, setSystemCount] = useState("1");
  const [ductType, setDuctType] = useState("flex_6");
  const [ductLength, setDuctLength] = useState("");
  const [registerCount, setRegisterCount] = useState("");
  const [returnCount, setReturnCount] = useState("");
  const [laborRate, setLaborRate] = useState("85");
  const [equipmentCost, setEquipmentCost] = useState("");

  const system = systemTypes[systemType];
  const duct = ductMaterials[ductType];
  const systems = parseInt(systemCount) || 1;
  const ductLen = parseFloat(ductLength) || 0;
  const registers = parseInt(registerCount) || 0;
  const returns = parseInt(returnCount) || 0;
  const lr = laborRate;
  const rate = parseFloat(lr) || 85;
  const ec = equipmentCost;
  const equipCost = parseFloat(ec) || 0;

  // Duct material
  const ductCost = ductLen * (duct?.costPerFt ?? 0) * 1.1; // 10% waste

  // Registers and returns
  const registerCost = registers * 35; // avg $35 per register + boot
  const returnCost = returns * 55; // avg $55 per return grille + box

  // Insulation (R-8 duct wrap, ~$0.50/ft for flex, $2/ft for sheet metal)
  const insulationCost = ductType.startsWith("flex") ? 0 : ductLen * 2.0;

  // Labor
  const installHours = (system?.avgInstallHrs ?? 16) * systems;
  const ductLaborHours = ductLen / 15; // ~15 LF per hour for duct installation
  const registerLaborHours = (registers + returns) * 0.5; // 30 min per register/return
  const totalLaborHours = installHours + ductLaborHours + registerLaborHours;
  const laborCost = totalLaborHours * rate;

  // Materials total
  const materialCost = ductCost + registerCost + returnCost + insulationCost + equipCost;
  const totalCost = laborCost + materialCost;

  const hasResult = ductLen > 0 || equipCost > 0;

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const fmtDec = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const inputClass =
    "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary";

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Equipment</h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-foreground">
            System Type
          </label>
          <select
            value={systemType}
            onChange={(e) => setSystemType(e.target.value)}
            className={inputClass}
          >
            {Object.entries(systemTypes).map(([key, sys]) => (
              <option key={key} value={key}>
                {sys.label} ({sys.costRange})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">
            Number of Systems
          </label>
          <input
            type="number"
            value={systemCount}
            onChange={(e) => setSystemCount(e.target.value)}
            placeholder="1"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">
            Equipment Cost ($)
          </label>
          <input
            type="number"
            value={equipmentCost}
            onChange={(e) => setEquipmentCost(e.target.value)}
            placeholder="6000"
            className={inputClass}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Your actual equipment cost
          </p>
        </div>
      </div>

      <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">
        Ductwork
      </h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="block text-sm font-medium text-foreground">
            Duct Type
          </label>
          <select
            value={ductType}
            onChange={(e) => setDuctType(e.target.value)}
            className={inputClass}
          >
            {Object.entries(ductMaterials).map(([key, mat]) => (
              <option key={key} value={key}>
                {mat.label} (${mat.costPerFt.toFixed(2)}/ft)
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">
            Total Duct Run (ft)
          </label>
          <input
            type="number"
            value={ductLength}
            onChange={(e) => setDuctLength(e.target.value)}
            placeholder="200"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">
            Supply Registers
          </label>
          <input
            type="number"
            value={registerCount}
            onChange={(e) => setRegisterCount(e.target.value)}
            placeholder="10"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">
            Return Grilles
          </label>
          <input
            type="number"
            value={returnCount}
            onChange={(e) => setReturnCount(e.target.value)}
            placeholder="3"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-6">
        <label className="block text-sm font-medium text-foreground">
          Labor Rate ($/hr)
        </label>
        <input
          type="number"
          value={laborRate}
          onChange={(e) => setLaborRate(e.target.value)}
          placeholder="85"
          className={`${inputClass} max-w-[200px]`}
        />
      </div>

      {hasResult && (
        <>
          <div className="mt-8 rounded-lg border border-border bg-muted/20 p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Estimate Summary
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <div className="text-sm text-muted-foreground">Equipment</div>
                <div className="text-xl font-bold text-foreground">
                  ${fmt(equipCost)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {systems}x {system?.label}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">
                  Duct & Registers
                </div>
                <div className="text-xl font-bold text-foreground">
                  ${fmt(ductCost + registerCost + returnCost + insulationCost)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {fmt(ductLen)} ft duct + {registers} supply + {returns} return
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Labor</div>
                <div className="text-xl font-bold text-foreground">
                  ${fmt(laborCost)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {fmtDec(totalLaborHours)} hrs @ ${fmt(rate)}/hr
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total</div>
                <div className="text-2xl font-bold text-secondary">
                  ${fmt(totalCost)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Before markup and overhead
                </div>
              </div>
            </div>
          </div>
          <ResultActions
            toolSlug="hvac-calculator"
            inputs={{ systemType, systemCount, ductType, ductLength, registerCount, returnCount, laborRate, equipmentCost }}
            outputs={{ materialCost, laborCost, totalCost, totalLaborHours }}
          />
        </>
      )}
    </div>
  );
}
